import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  LogOut,
  Phone,
  Mail,
  MapPin,
  Loader2,
  RefreshCcw,
  Check,
  X,
  CircleDot,
  Download,
} from "lucide-react";
import { CaptionPresets } from "../components/admin/CaptionPresets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "stac_admin_token";

const STATUS_META = {
  pending: { label: "Pending", color: "text-amber-300 border-amber-300/40" },
  confirmed: { label: "Confirmed", color: "text-emerald-300 border-emerald-300/40" },
  cancelled: { label: "Cancelled", color: "text-white/40 border-white/20 line-through" },
  completed: { label: "Completed", color: "text-sky-300 border-sky-300/40" },
};

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const authedClient = useMemo(() => {
    if (!token) return null;
    return axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  const loadAll = useCallback(async (client) => {
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        client.get("/admin/bookings"),
        client.get("/admin/stats"),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired — please log in again.");
      } else {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authedClient) return;
    loadAll(authedClient);
  }, [authedClient, loadAll]);

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (!authedClient) return;
    try {
      await authedClient.patch(`/admin/bookings/${id}`, { status: newStatus });
      toast.success(`Marked as ${newStatus}.`);
      loadAll(authedClient);
    } catch {
      toast.error("Could not update booking.");
    }
  };

  const exportCsv = async () => {
    if (!authedClient) return;
    try {
      const res = await authedClient.get("/admin/bookings/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stac-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bookings exported to CSV.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
    setStats(null);
    navigate("/admin");
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <form
          onSubmit={submitLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-white/10 bg-[#0E0E0E] p-10"
        >
          <div className="font-display text-2xl font-light tracking-[0.18em]">STAC</div>
          <div className="overline mt-2 mb-10">— Admin</div>

          <label htmlFor="admin-pw" className="text-xs tracking-[0.18em] uppercase text-white/55 mb-2 block">
            Password
          </label>
          <input
            id="admin-pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoFocus
            data-testid="admin-password-input"
            className="w-full bg-[#080808] border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-white/55 focus:outline-none transition-colors"
          />

          <button
            type="submit"
            disabled={loggingIn || !password}
            data-testid="admin-login-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-white text-black px-6 py-3.5 text-xs font-medium tracking-[0.22em] uppercase hover:bg-white/85 disabled:opacity-40 transition-colors"
          >
            {loggingIn ? <Loader2 size={14} className="animate-spin" /> : null}
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>

          <a href="/" className="mt-8 block text-xs text-white/40 hover:text-white/70 text-center tracking-widest uppercase">
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  const filtered = bookings.filter((b) => {
    if (filter === "all") return true;
    if (filter === "upcoming") {
      const today = new Date().toISOString().slice(0, 10);
      return b.date >= today && b.status !== "cancelled";
    }
    return b.status === filter;
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-[#0A0A0A]/85 backdrop-blur-xl z-40">
        <div>
          <div className="font-display text-xl tracking-[0.18em] font-light">STAC</div>
          <div className="overline mt-1">— Admin Dashboard</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/45 px-4 py-2.5 text-xs tracking-[0.18em] uppercase transition-colors"
          >
            <Download size={13} /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button
            onClick={() => loadAll(authedClient)}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/45 px-4 py-2.5 text-xs tracking-[0.18em] uppercase transition-colors"
          >
            <RefreshCcw size={13} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/45 px-4 py-2.5 text-xs tracking-[0.18em] uppercase transition-colors"
          >
            <LogOut size={13} /> Log out
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10" data-testid="admin-stats">
          <Stat label="Total bookings" value={stats?.total_bookings ?? "—"} />
          <Stat label="Upcoming" value={stats?.upcoming ?? "—"} />
          <Stat label="Paid deposits" value={stats?.paid ?? "—"} />
          <Stat label="Deposits collected" value={stats ? `£${(stats.deposits_total_gbp ?? 0).toFixed(2)}` : "—"} />
        </div>

        {/* Instagram caption presets */}
        <CaptionPresets />

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "upcoming", "pending", "confirmed", "completed", "cancelled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border transition-colors ${
                filter === f
                  ? "bg-white text-black border-white"
                  : "border-white/15 text-white/75 hover:border-white/45"
              }`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto text-xs text-white/45 self-center">
            {loading ? "Loading…" : `${filtered.length} of ${bookings.length} shown`}
          </span>
        </div>

        {/* Bookings list */}
        {filtered.length === 0 && !loading && (
          <div className="border border-white/10 p-10 text-center text-white/55 text-sm">
            No bookings to show in this view.
          </div>
        )}

        <div className="space-y-3" data-testid="admin-bookings-list">
          {filtered.map((b) => (
            <BookingRow key={b.id} b={b} onStatus={updateStatus} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0E0E0E] p-6">
      <div className="overline mb-3">{label}</div>
      <div className="font-display text-3xl font-light">{value}</div>
    </div>
  );
}

function BookingRow({ b, onStatus }) {
  const meta = STATUS_META[b.status] || STATUS_META.pending;
  const paid = b.payment_status === "paid";
  let dateStr = b.date;
  try {
    dateStr = format(new Date(b.date), "EEE d MMM yyyy");
  } catch {
    // fall back to the raw date string
  }
  return (
    <article
      data-testid={`admin-booking-${b.id}`}
      className="grid md:grid-cols-12 gap-4 md:gap-6 items-start border border-white/10 bg-[#0E0E0E] hover:bg-[#121212] transition-colors p-5 md:p-6"
    >
      {/* Date + time */}
      <div className="md:col-span-2">
        <div className="flex items-center gap-2 text-xs text-white/55 tracking-widest uppercase mb-1">
          <CalendarIcon size={12} /> {b.time}
        </div>
        <div className="font-display text-lg font-light">{dateStr}</div>
      </div>

      {/* Customer */}
      <div className="md:col-span-3">
        <div className="font-medium">{b.name}</div>
        <div className="text-xs text-white/55 mt-1 flex items-center gap-1.5">
          <Phone size={11} /> {b.phone}
        </div>
        <div className="text-xs text-white/55 flex items-center gap-1.5 mt-0.5 break-all">
          <Mail size={11} /> {b.email}
        </div>
      </div>

      {/* Vehicle / package / location */}
      <div className="md:col-span-4 space-y-1.5 text-sm">
        <div className="text-white/80">{b.vehicle}</div>
        <div className="text-xs text-white/55 uppercase tracking-wider">{b.package_id.replace(/-/g, " ")}</div>
        <div className="text-xs text-white/55 flex items-start gap-1.5">
          <MapPin size={11} className="mt-0.5 flex-shrink-0" /> {b.location}
        </div>
        {b.notes && <div className="text-xs text-white/45 italic">Note: {b.notes}</div>}
        {b.addons && b.addons.length > 0 && (
          <div className="text-xs text-amber-200/80 flex flex-wrap gap-1.5 pt-1">
            {b.addons.map((a) => (
              <span key={a} className="inline-flex items-center border border-amber-200/30 px-1.5 py-0.5 uppercase tracking-wider">
                + {a.replace(/-/g, " ")}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status pills */}
      <div className="md:col-span-3 flex flex-col items-start md:items-end gap-2">
        <div className="flex flex-wrap gap-2 md:justify-end">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border ${meta.color}`}>
            <CircleDot size={10} /> {meta.label}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border ${
              paid ? "text-emerald-300 border-emerald-300/40" : "text-white/40 border-white/20"
            }`}
          >
            {paid ? <Check size={10} /> : <X size={10} />} {paid ? "Deposit paid" : "Unpaid"}
          </span>
          {b.first_time && (
            <span
              data-testid={`admin-first-wash-${b.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border text-emerald-200 border-emerald-200/40 bg-emerald-300/10"
            >
              First wash · 50% off
            </span>
          )}
          {b.referral_discount > 0 && (
            <span
              data-testid={`admin-referral-${b.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border text-sky-200 border-sky-200/40 bg-sky-300/10"
            >
              £{b.referral_discount} referral credit
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 md:justify-end mt-1">
          {b.status !== "confirmed" && b.status !== "cancelled" && (
            <button
              onClick={() => onStatus(b.id, "confirmed")}
              data-testid={`admin-confirm-${b.id}`}
              className="text-[10px] tracking-[0.2em] uppercase border border-white/15 hover:border-white/45 px-3 py-1.5 transition-colors"
            >
              Confirm
            </button>
          )}
          {b.status !== "completed" && b.status !== "cancelled" && (
            <button
              onClick={() => onStatus(b.id, "completed")}
              data-testid={`admin-complete-${b.id}`}
              className="text-[10px] tracking-[0.2em] uppercase border border-white/15 hover:border-white/45 px-3 py-1.5 transition-colors"
            >
              Mark done
            </button>
          )}
          {b.status !== "cancelled" && (
            <button
              onClick={() => onStatus(b.id, "cancelled")}
              data-testid={`admin-cancel-${b.id}`}
              className="text-[10px] tracking-[0.2em] uppercase border border-red-400/25 text-red-300/80 hover:border-red-400/60 px-3 py-1.5 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
